import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell, faCircleCheck, faComment,
  faGear, faHashtag, faTriangleExclamation, faUsers, faReply,
  faFire, faArrowTrendUp, faVolumeXmark, faUser, faChevronDown,
  faChevronUp, faFlask, faCircleInfo, faFaceAngry, faThumbsUp,
} from '@fortawesome/free-solid-svg-icons';
import { CONFLICT_WORDS, NEGATIVE_EMOJIS, isNegativeEmoji } from '@/services/messaging-sentiment';
import type { AnomalyRelatedMessage, MessagingAnomaly, ChannelActivityStats, MemberActivityStats, MessagingSnapshot, SlackSettings, TeamsSettings, MessagingMessage } from '@/services/messaging-types';
import { getMessagesByDateRange } from '@/services/messaging-storage';
import { messagingMonitor } from '@/services/messaging-monitor';
import { toLocalDateStr, subDays } from '@/lib/utils';
import { toEmoji } from '@/lib/emoji';

export const cx = (...a: (string|false|undefined)[]) => a.filter(Boolean).join(' ');

// ─── Sparkline ────────────────────────────────────────────────────────────────
export function Sparkline({ data }: { data: Record<string, number> }) {
  const vals = Object.entries(data).sort(([a],[b])=>a.localeCompare(b)).slice(-14);
  if (!vals.length) return null;
  const max = Math.max(...vals.map(([,v])=>v), 1);
  return (
    <div className="flex items-end gap-px h-6">
      {vals.map(([d,v]) => <div key={d} className="w-1.5 rounded-sm bg-indigo-400 dark:bg-indigo-500 opacity-80 flex-shrink-0" style={{height:`${Math.max(2,(v/max)*24)}px`}}/>)}
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
export function Stat({ label, value, icon, bg }: { label:string; value:string|number; icon:React.ReactNode; bg:string }) {
  return (
    <div className="bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 flex items-center gap-3">
      <div className={cx('w-10 h-10 rounded-lg flex items-center justify-center', bg)}>{icon}</div>
      <div><div className="text-xl font-bold text-gray-900 dark:text-white">{value}</div><div className="text-xs text-gray-500 dark:text-slate-400">{label}</div></div>
    </div>
  );
}

// ─── Card wrapper ─────────────────────────────────────────────────────────────
export function Card({ children, title, sub }: { children:React.ReactNode; title?:string; sub?:string }) {
  return (
    <div className="bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
      {title && (
        <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700/30">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
          {sub && <p className="text-xs text-gray-400 dark:text-slate-500">{sub}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── Channel table ────────────────────────────────────────────────────────────
export function ChannelTable({ rows, anomalies = [] }: { rows: ChannelActivityStats[]; anomalies?: MessagingAnomaly[] }) {
  if (!rows.length) return <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-8">No channel data yet — start monitoring to collect activity.</p>;
  const anomalyCountFor = (channelId: string) => anomalies.filter(a => a.channelId === channelId).length;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-gray-200 dark:border-slate-700/50">
          {[['Channel','text-left'],['Messages','text-right'],['Reactions','text-right hidden sm:table-cell'],['Members','text-right'],['Anomalies','text-right hidden md:table-cell'],['Last Active','text-right hidden md:table-cell'],['Trend','text-left hidden lg:table-cell']].map(([h,c]) => (
            <th key={h} className={cx('py-2 px-3 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-widest', c)}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {[...rows].sort((a,b)=>b.messageCount-a.messageCount).map(ch => {
            const aCount = anomalyCountFor(ch.channelId);
            return (
              <tr key={`${ch.teamId??''}:${ch.channelId}`} className="border-b border-gray-100 dark:border-slate-700/30 hover:bg-gray-50 dark:hover:bg-slate-800/40">
                <td className="py-2.5 px-3 font-medium text-gray-900 dark:text-white">
                  <span className="flex items-center gap-1.5">
                    <FontAwesomeIcon icon={faHashtag} className="h-3 w-3 text-gray-400 flex-shrink-0"/>
                    {ch.channelName}
                    {ch.teamName && <span className="text-xs text-gray-400 ml-1">({ch.teamName})</span>}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-slate-300">{ch.messageCount.toLocaleString()}</td>
                <td className="py-2.5 px-3 text-right text-violet-600 dark:text-violet-400 hidden sm:table-cell">{(ch.totalReactions ?? 0)}</td>
                <td className="py-2.5 px-3 text-right text-gray-500 dark:text-slate-400">{ch.uniqueAuthors}</td>
                <td className="py-2.5 px-3 text-right hidden md:table-cell">
                  {aCount > 0
                    ? <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">{aCount}</span>
                    : <span className="text-gray-300 dark:text-slate-600">—</span>}
                </td>
                <td className="py-2.5 px-3 text-right text-gray-400 text-xs hidden md:table-cell">
                  {ch.lastMessageAt ? new Date(ch.lastMessageAt).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit'}) : '—'}
                </td>
                <td className="py-2.5 px-3 hidden lg:table-cell"><Sparkline data={ch.dailyCounts}/></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Member table ─────────────────────────────────────────────────────────────
export function MemberTable({ rows, anomalies = [] }: { rows: MemberActivityStats[]; anomalies?: MessagingAnomaly[] }) {
  if (!rows.length) return <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-8">No member data yet.</p>;
  const anomalyCountFor = (memberId: string, memberName: string) =>
    anomalies.filter(a =>
      a.memberId === memberId ||
      a.relatedMessages?.some(m => m.authorName === memberName)
    ).length;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-gray-200 dark:border-slate-700/50">
          {[['Member','text-left'],['Messages','text-right'],['Reactions','text-right hidden sm:table-cell'],['Replies','text-right'],['Channels','text-right hidden md:table-cell'],['Anomalies','text-right hidden md:table-cell'],['Last Seen','text-right hidden md:table-cell'],['Trend','text-left hidden lg:table-cell']].map(([h,c]) => (
            <th key={h} className={cx('py-2 px-3 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-widest', c)}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {[...rows].sort((a,b)=>b.messageCount-a.messageCount).slice(0,20).map((m,i) => {
            const hue = m.memberId.split('').reduce((a,c)=>a+c.charCodeAt(0),0)%360;
            const ini = m.memberName.split(/\s+/).slice(0,2).map(p=>p[0]?.toUpperCase()??'').join('')||'?';
            const aCount = anomalyCountFor(m.memberId, m.memberName);
            return (
              <tr key={m.memberId} className="border-b border-gray-100 dark:border-slate-700/30 hover:bg-gray-50 dark:hover:bg-slate-800/40">
                <td className="py-2.5 px-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{background:`hsl(${hue},55%,${i<3?42:50}%)`}}>{ini}</div>
                    <span className="font-medium text-gray-900 dark:text-white">{m.memberName}</span>
                    {i<3 && <span className="text-[9px] px-1 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 font-semibold">TOP</span>}
                  </div>
                </td>
                <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-slate-300">{m.messageCount.toLocaleString()}</td>
                <td className="py-2.5 px-3 text-right text-violet-600 dark:text-violet-400 hidden sm:table-cell">{(m.totalReactions ?? 0)}</td>
                <td className="py-2.5 px-3 text-right text-gray-500 dark:text-slate-400">{m.replyCount}</td>
                <td className="py-2.5 px-3 text-right text-gray-500 dark:text-slate-400 hidden md:table-cell">{m.activeChannelCount}</td>
                <td className="py-2.5 px-3 text-right hidden md:table-cell">
                  {aCount > 0
                    ? <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">{aCount}</span>
                    : <span className="text-gray-300 dark:text-slate-600">—</span>}
                </td>
                <td className="py-2.5 px-3 text-right text-gray-400 text-xs hidden md:table-cell">
                  {m.lastActiveAt ? new Date(m.lastActiveAt).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit'}) : '—'}
                </td>
                <td className="py-2.5 px-3 hidden lg:table-cell"><Sparkline data={m.dailyCounts}/></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Anomaly helpers ──────────────────────────────────────────────────────────
function anomalyIcon(type: MessagingAnomaly['type']) {
  switch (type) {
    case 'conflict_detected':      return faFire;
    case 'heated_thread':          return faFire;
    case 'negative_reaction_spike': return faFaceAngry;
    case 'sudden_spike':           return faArrowTrendUp;
    case 'sudden_silence':         return faVolumeXmark;
    case 'no_activity':            return faVolumeXmark;
    case 'single_author_channel':  return faUser;
    default:                       return faTriangleExclamation;
  }
}
function anomalyTypeLabel(type: MessagingAnomaly['type']): string {
  switch (type) {
    case 'conflict_detected':       return 'Conflict';
    case 'heated_thread':           return 'Heated Thread';
    case 'negative_reaction_spike': return 'Reaction Spike';
    case 'sudden_spike':            return 'Sudden Spike';
    case 'sudden_silence':          return 'Sudden Silence';
    case 'no_activity':             return 'No Activity';
    case 'single_author_channel':   return 'Single Author';
    case 'low_team_engagement':     return 'Low Engagement';
    default:                        return type;
  }
}

// ─── Slack text renderer ────────────────────────────────────────────────────
const SLACK_PATTERN = /```([\/\S\s]*?)```|`([^`\n]+)`|<([^>]+)>|:([a-zA-Z0-9_+\-]+(?:::?skin-tone-[2-6])?):|(\*[^*\n]+\*)|(_[^_\n]+_)|(~[^~\n]+~)|&gt;|&lt;|&amp;|\n/g;

function SlackText({ text }: { text: string }) {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  SLACK_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = SLACK_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const [full, codeBlock, inlineCode, ref, emojiName, bold, italic, strike] = match;
    if (codeBlock !== undefined) {
      nodes.push(<code key={key++} className="block bg-gray-100 dark:bg-slate-700 rounded px-1.5 py-1 text-[10px] font-mono whitespace-pre-wrap my-0.5">{codeBlock.trim()}</code>);
    } else if (inlineCode !== undefined) {
      nodes.push(<code key={key++} className="bg-gray-100 dark:bg-slate-700 rounded px-1 text-[10px] font-mono">{inlineCode}</code>);
    } else if (ref !== undefined) {
      const pipe = ref.indexOf('|');
      const id = pipe >= 0 ? ref.slice(0, pipe) : ref;
      const label = pipe >= 0 ? ref.slice(pipe + 1) : ref;
      if (id.startsWith('http') || id.startsWith('mailto')) {
        nodes.push(<a key={key++} href={id} target="_blank" rel="noopener noreferrer" className="text-indigo-500 underline break-all">{label}</a>);
      } else if (id.startsWith('@') || /^[UW][A-Z0-9]+$/.test(id)) {
        nodes.push(<span key={key++} className="text-indigo-500 font-medium">@{label !== id ? label : id.replace(/^[UW]/, '')}</span>);
      } else if (id.startsWith('#') || /^[C][A-Z0-9]+$/.test(id)) {
        nodes.push(<span key={key++} className="text-indigo-500 font-medium">#{label !== id ? label : id.slice(1)}</span>);
      } else if (id.startsWith('!')) {
        nodes.push(<span key={key++} className="text-indigo-500 font-medium">@{id.slice(1)}</span>);
      } else {
        nodes.push(label);
      }
    } else if (emojiName !== undefined) {
      const rendered = toEmoji(emojiName);
      nodes.push(rendered !== `:${emojiName}:` ? rendered : full);
    } else if (bold !== undefined) {
      nodes.push(<strong key={key++} className="font-semibold">{bold.slice(1, -1)}</strong>);
    } else if (italic !== undefined) {
      nodes.push(<em key={key++}>{italic.slice(1, -1)}</em>);
    } else if (strike !== undefined) {
      nodes.push(<del key={key++}>{strike.slice(1, -1)}</del>);
    } else if (full === '&gt;') {
      nodes.push('>');
    } else if (full === '&lt;') {
      nodes.push('<');
    } else if (full === '&amp;') {
      nodes.push('&');
    } else if (full === '\n') {
      nodes.push(<br key={key++} />);
    }
    lastIndex = SLACK_PATTERN.lastIndex;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return <>{nodes}</>;
}

// ─── Related message thread snippet ──────────────────────────────────────────
function RelatedMessages({ msgs, isNegReaction }: { msgs: AnomalyRelatedMessage[]; isNegReaction?: boolean }) {
  return (
    <div className="mt-2.5 rounded-lg border border-current/10 bg-white/60 dark:bg-slate-900/40 overflow-hidden divide-y divide-current/5">
      {msgs.map((m, i) => {
        const hue = m.authorName.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
        const ini = m.authorName.split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('') || '?';
        const hasNegReaction = m.reactions?.some(r => isNegativeEmoji(r.emoji));
        return (
          <div key={i} className={cx(
            'flex items-start gap-2 px-3 py-2 text-xs',
            isNegReaction && hasNegReaction && 'bg-red-50/60 dark:bg-red-500/5'
          )}>
            <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0 mt-0.5"
              style={{ background: `hsl(${hue},50%,46%)` }}>{ini}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-1.5 mb-0.5">
                <span className="font-semibold text-gray-900 dark:text-white text-[11px]">{m.authorName}</span>
                <span className="text-[10px] text-gray-400 dark:text-slate-500">
                  {new Date(m.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <p className="text-gray-700 dark:text-slate-300 leading-relaxed break-words"><SlackText text={m.text} /></p>
              {m.reactions && m.reactions.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {m.reactions.map(r => (
                    <span key={r.emoji} className={cx(
                      'inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full text-[9px]',
                      isNegativeEmoji(r.emoji)
                        ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
                        : 'bg-gray-100 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400'
                    )}>{ toEmoji(r.emoji)} {r.count}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Anomaly list ─────────────────────────────────────────────────────────────
export function AnomalyList({ items }: { items: MessagingAnomaly[] }) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (i: number) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  if (!items.length) return (
    <div className="flex flex-col items-center py-10 text-center">
      <FontAwesomeIcon icon={faCircleCheck} className="h-8 w-8 text-emerald-400 mb-3"/>
      <p className="text-sm font-medium text-gray-700 dark:text-slate-300">No anomalies detected</p>
      <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">All communication patterns look normal.</p>
    </div>
  );
  const sevCls = (s: MessagingAnomaly['severity']) =>
    s==='high'   ? 'text-red-600 bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20'
    : s==='medium' ? 'text-amber-600 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20'
    : 'text-slate-500 bg-slate-50 dark:bg-slate-500/10 border-slate-200 dark:border-slate-500/20';
  const iconCls = (s: MessagingAnomaly['severity'], type: MessagingAnomaly['type']) => {
    if (type === 'conflict_detected' || type === 'heated_thread')
      return s === 'high' ? 'text-red-500' : 'text-amber-500';
    return s === 'high' ? 'text-red-500' : s === 'medium' ? 'text-amber-500' : 'text-slate-400';
  };
  const sorted = [...items].sort((a, b) => {
    const rank = { high: 0, medium: 1, low: 2 };
    return rank[a.severity] - rank[b.severity];
  });
  return (
    <div className="space-y-2">
      {sorted.map((a, i) => {
        const hasContext = (a.relatedMessages?.length ?? 0) > 0;
        const isOpen = expanded.has(i);
        const isNegReaction = a.type === 'negative_reaction_spike';
        return (
          <div key={i} className={cx('rounded-xl border overflow-hidden', sevCls(a.severity))}>
            <div className="flex items-start gap-3 p-3.5">
              <FontAwesomeIcon icon={anomalyIcon(a.type)} className={cx('h-4 w-4 flex-shrink-0 mt-0.5', iconCls(a.severity, a.type))}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">{anomalyTypeLabel(a.type)}</span>
                  {a.channelName && <span className="text-[10px] opacity-50">#{a.channelName}</span>}
                </div>
                <p className="text-sm font-medium leading-snug">{a.description}</p>
                <div className="flex items-center gap-3 mt-0.5">
                  <p className="text-[10px] opacity-50">{new Date(a.detectedAt).toLocaleTimeString('en-AU')}</p>
                  {hasContext && (
                    <button onClick={() => toggle(i)}
                      className="text-[10px] opacity-60 hover:opacity-100 flex items-center gap-1 transition-opacity">
                      <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="h-2.5 w-2.5"/>
                      {isOpen ? 'hide context' : `show ${a.relatedMessages!.length} message${a.relatedMessages!.length > 1 ? 's' : ''}`}
                    </button>
                  )}
                </div>
                {isOpen && hasContext && (
                  <RelatedMessages msgs={a.relatedMessages!} isNegReaction={isNegReaction}/>
                )}
              </div>
              <span className={cx('text-[10px] px-1.5 py-0.5 rounded font-bold border uppercase tracking-wide flex-shrink-0',
                a.severity==='high' ? 'border-red-300 dark:border-red-500/40'
                : a.severity==='medium' ? 'border-amber-300 dark:border-amber-500/40'
                : 'border-slate-300 dark:border-slate-500/40'
              )}>{a.severity}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Detector Reference ───────────────────────────────────────────────────────

const DETECTOR_DOCS = [
  {
    type: 'conflict_detected' as const,
    emoji: '🔥',
    label: 'Conflict Detected',
    severityRange: 'medium → high',
    severityCls: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400',
    description: 'Fires when ≥15% of messages in a channel today contain conflict language (disagreement, frustration, profanity) and at least 2 messages are flagged.',
    threshold: '≥15% flagged messages in channel AND ≥2 flagged msgs (high: ≥30%)',
    forceIt: 'Send messages containing "I disagree", "fuck", "ridiculous", or "wtf". Just 2 flagged msgs out of ~13 will trigger it.',
  },
  {
    type: 'heated_thread' as const,
    emoji: '💢',
    label: 'Heated Thread',
    severityRange: 'medium → high',
    severityCls: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400',
    description: 'Detects rapid back-and-forth in a thread between 2+ people with conflict language. "Rapid" means avg gap under 5 minutes between replies.',
    threshold: '≥4 replies, ≥2 authors, ≥2 conflict msgs, avg gap <5 min',
    forceIt: 'Start a thread, have 2+ people exchange ≥4 quick replies where at least 2 contain "disagree" or "wtf".',
  },
  {
    type: 'sudden_spike' as const,
    emoji: '📈',
    label: 'Sudden Spike',
    severityRange: 'medium',
    severityCls: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400',
    description: 'A channel receives more than 3× its 7-day average message volume AND more than 20 messages today.',
    threshold: '>3× baseline AND >20 messages today',
    forceIt: 'If baseline avg is ~5 msgs/day, send 20+ messages in that channel today.',
  },
  {
    type: 'no_activity' as const,
    emoji: '🔇',
    label: 'No Activity',
    severityRange: 'medium → high',
    severityCls: 'bg-slate-100 dark:bg-slate-500/20 text-slate-600 dark:text-slate-400',
    description: 'A channel that normally averages >5 messages per day had zero messages today.',
    threshold: 'Baseline >5 msgs/day and 0 today (high: baseline >20)',
    forceIt: 'Be active in a channel for a day (6+ msgs), then don\'t post the next day.',
  },
  {
    type: 'sudden_silence' as const,
    emoji: '😶',
    label: 'Sudden Silence',
    severityRange: 'low',
    severityCls: 'bg-gray-100 dark:bg-gray-500/20 text-gray-600 dark:text-gray-400',
    description: 'A specific member who normally posts >3 messages per day has sent nothing today.',
    threshold: 'Baseline >3 msgs/day and 0 messages today',
    forceIt: 'Post 4+ messages one day as a user, then don\'t post the following day.',
  },
  {
    type: 'single_author_channel' as const,
    emoji: '👤',
    label: 'Single Author',
    severityRange: 'low',
    severityCls: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400',
    description: 'A channel has >5 messages today but only one person is posting — may indicate a broadcast channel, spam, or an ignored thread.',
    threshold: '1 unique author AND >5 messages in the channel today',
    forceIt: 'Send 6+ messages to any channel without anyone else replying.',
  },
  {
    type: 'negative_reaction_spike' as const,
    emoji: '👎',
    label: 'Negative Reaction Spike',
    severityRange: 'medium → high',
    severityCls: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400',
    description: 'A specific message has received an unusually high proportion of negative reactions (👎 😤 😡 🤦 💀 etc.) relative to its total reactions. Requires reactions:read scope.',
    threshold: '≥3 negative reactions AND ≥40% of all reactions are negative (high: ≥5 negative or ≥70%)',
    forceIt: 'Post a message and have 3+ people react with 👎 or 😤. Ensure reactions:read is in your bot\'s OAuth scopes.',
  },
];

const SAMPLE_KEYWORDS = ['disagree', 'frustrated', "you're wrong", 'ridiculous', 'wtf', 'unacceptable', 'seriously?', 'come on'];
const SAMPLE_NEG_EMOJIS = ['-1', 'rage', 'disappointed', 'facepalm', 'no_entry_sign', 'skull'];

export function DetectorReference() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/80 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircleInfo} className="h-3.5 w-3.5 text-indigo-500"/>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">How anomalies are detected</p>
          <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 rounded font-medium">
            {DETECTOR_DOCS.length} detectors
          </span>
        </div>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="h-3 w-3 text-gray-400"/>
      </button>

      {open && (
        <div className="border-t border-gray-100 dark:border-slate-700/30">
          {/* Conflict keyword list */}
          <div className="px-4 py-3 bg-amber-50/60 dark:bg-amber-500/5 border-b border-amber-100 dark:border-amber-500/10">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1.5">🔍 Conflict keywords ({CONFLICT_WORDS.length} phrases)</p>
            <div className="flex flex-wrap gap-1">
              {SAMPLE_KEYWORDS.map(w => (
                <code key={w} className="text-[10px] px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-500/30 rounded text-amber-700 dark:text-amber-300">{w}</code>
              ))}
              <span className="text-[10px] px-1.5 py-0.5 text-amber-600 dark:text-amber-400 italic">+{CONFLICT_WORDS.length - SAMPLE_KEYWORDS.length} more</span>
            </div>
          </div>
          {/* Negative emoji list */}
          <div className="px-4 py-3 bg-red-50/60 dark:bg-red-500/5 border-b border-red-100 dark:border-red-500/10">
            <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1.5">👎 Negative reaction emojis ({NEGATIVE_EMOJIS.size} tracked)</p>
            <div className="flex flex-wrap gap-1">
              {SAMPLE_NEG_EMOJIS.map(e => (
                <code key={e} className="text-[10px] px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-red-200 dark:border-red-500/30 rounded text-red-600 dark:text-red-300">:{e}:</code>
              ))}
              <span className="text-[10px] px-1.5 py-0.5 text-red-500 dark:text-red-400 italic">+{NEGATIVE_EMOJIS.size - SAMPLE_NEG_EMOJIS.length} more</span>
            </div>
          </div>

          {/* Detector cards */}
          <div className="divide-y divide-gray-100 dark:divide-slate-700/30">
            {DETECTOR_DOCS.map(d => (
              <div key={d.type} className="px-4 py-3.5">
                <div className="flex items-start gap-3">
                  <span className="text-base flex-shrink-0 mt-0.5">{d.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{d.label}</span>
                      <span className={cx('text-[10px] px-1.5 py-0.5 rounded font-medium', d.severityCls)}>{d.severityRange}</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed mb-1.5">{d.description}</p>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-start gap-1.5">
                        <span className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-widest flex-shrink-0 mt-0.5">Threshold</span>
                        <code className="text-[10px] text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-1.5 py-0.5 rounded leading-snug">{d.threshold}</code>
                      </div>
                      <div className="flex items-start gap-1.5">
                        <FontAwesomeIcon icon={faFlask} className="h-2.5 w-2.5 text-emerald-500 flex-shrink-0 mt-1"/>
                        <p className="text-[10px] text-emerald-700 dark:text-emerald-400 leading-snug">{d.forceIt}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Message Feed ─────────────────────────────────────────────────────────────
export function MessageFeed({ messages, channelStats = [] }: { messages: MessagingMessage[]; channelStats?: { channelId: string; channelName: string }[] }) {
  const channelNameMap = new Map(channelStats.map(c => [c.channelId, c.channelName]));
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const toggleThread = (id: string) => setExpandedThreads(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  // Build a lookup of threadId → reply messages for inline expansion
  const threadRepliesMap = new Map<string, MessagingMessage[]>();
  for (const m of messages) {
    if (m.isReply && m.threadId) {
      if (!threadRepliesMap.has(m.threadId)) threadRepliesMap.set(m.threadId, []);
      threadRepliesMap.get(m.threadId)!.push(m);
    }
  }
  if (!messages.length) return (
    <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-8">No messages in this period.</p>
  );

  // Group by local date, sorted newest first
  const byDate = new Map<string, MessagingMessage[]>();
  for (const m of messages) {
    const day = new Date(m.timestamp).toLocaleDateString('en-CA');
    if (!byDate.has(day)) byDate.set(day, []);
    byDate.get(day)!.push(m);
  }
  const sortedDates = [...byDate.keys()].sort((a, b) => b.localeCompare(a));

  const today = toLocalDateStr();
  const yesterday = toLocalDateStr(subDays(new Date(), 1));

  return (
    <div className="space-y-4">
      {sortedDates.map(date => {
        const dayMsgs = [...(byDate.get(date) ?? [])].sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        const label = date === today ? 'Today' : date === yesterday ? 'Yesterday'
          : new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
        return (
          <div key={date}>
            <div className="sticky top-0 z-10 py-1.5 mb-2 bg-gray-50 dark:bg-slate-900/80 backdrop-blur-sm">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-slate-400">{label}</span>
            </div>
            <div className="space-y-1">
              {dayMsgs.map(msg => {
                const hue = msg.authorId.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
                const ini = msg.authorName.split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('') || '?';
                return (
                  <div key={`${msg.channelId}:${msg.id}`}
                    className={cx(
                      'flex items-start gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800/50 transition-colors group',
                      msg.isReply && 'ml-6 border-l-2 border-gray-200 dark:border-slate-700 pl-3 rounded-l-none'
                    )}>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 mt-0.5"
                      style={{ background: `hsl(${hue},55%,46%)` }}>{ini}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-gray-900 dark:text-white">{msg.authorName}</span>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500">
                          {new Date(msg.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500 flex items-center gap-1">
                          <FontAwesomeIcon icon={faHashtag} className="h-2.5 w-2.5" />{channelNameMap.get(msg.channelId) ?? msg.channelId}
                        </span>
                        {msg.isReply && (
                          <span className="text-[10px] text-indigo-400 flex items-center gap-1">
                            <FontAwesomeIcon icon={faReply} className="h-2.5 w-2.5" />reply
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-700 dark:text-slate-300 mt-0.5 leading-relaxed break-words"><SlackText text={msg.text} /></p>
                      {msg.reactions && msg.reactions.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {msg.reactions.map(r => {
                            const isNeg = isNegativeEmoji(r.emoji);
                            return (
                              <span key={r.emoji} className={cx(
                                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px]',
                                isNeg
                                  ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/30'
                                  : 'bg-gray-100 dark:bg-slate-700/50 text-gray-600 dark:text-slate-300'
                              )}>{toEmoji(r.emoji)} {r.count}</span>
                            );
                          })}
                        </div>
                      )}
                      {!msg.isReply && msg.replyCount && msg.replyCount > 0 ? (
                        <>
                          <button
                            onClick={() => toggleThread(msg.id)}
                            className="text-[10px] text-indigo-500 dark:text-indigo-400 mt-0.5 flex items-center gap-1 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                          >
                            <FontAwesomeIcon icon={expandedThreads.has(msg.id) ? faChevronUp : faReply} className="h-2.5 w-2.5" />
                            {msg.replyCount} {msg.replyCount === 1 ? 'reply' : 'replies'}
                            {expandedThreads.has(msg.id) ? ' ▲' : ' ▼'}
                          </button>
                          {expandedThreads.has(msg.id) && (() => {
                            const replies = threadRepliesMap.get(msg.id) ?? [];
                            if (replies.length === 0) return (
                              <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1 ml-1 italic">Replies not yet loaded — poll again to fetch them.</p>
                            );
                            return (
                              <div className="mt-1.5 ml-1 border-l-2 border-indigo-200 dark:border-indigo-500/30 pl-3 space-y-1.5">
                                {[...replies].sort((a,b) => a.timestamp.localeCompare(b.timestamp)).map(r => {
                                  const rHue = r.authorId.split('').reduce((a,c) => a + c.charCodeAt(0), 0) % 360;
                                  const rIni = r.authorName.split(/\s+/).slice(0,2).map(p => p[0]?.toUpperCase() ?? '').join('') || '?';
                                  return (
                                    <div key={r.id} className="flex items-start gap-2">
                                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0 mt-0.5" style={{ background: `hsl(${rHue},55%,46%)` }}>{rIni}</div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-1.5">
                                          <span className="text-[11px] font-semibold text-gray-900 dark:text-white">{r.authorName}</span>
                                          <span className="text-[10px] text-gray-400 dark:text-slate-500">{new Date(r.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                        <p className="text-[11px] text-gray-700 dark:text-slate-300 leading-relaxed break-words"><SlackText text={r.text} /></p>
                                        {r.reactions && r.reactions.length > 0 && (
                                          <div className="flex flex-wrap gap-1 mt-0.5">
                                            {r.reactions.map(rx => (
                                              <span key={rx.emoji} className={cx('inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full text-[9px]',
                                                isNegativeEmoji(rx.emoji) ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400' : 'bg-gray-100 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400'
                                              )}>{toEmoji(rx.emoji)} {rx.count}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Platform dashboard (shared by SlackView + TeamsView) ─────────────────────

type Tab = 'overview' | 'channels' | 'members' | 'anomalies' | 'messages';

interface PlatformDashboardProps {
  platformLabel: string;
  tokenHint: string;
  platform: 'slack' | 'teams';
  settings: SlackSettings | TeamsSettings | null;
  snapshots: MessagingSnapshot[];
  lookbackDays?: number;
  onOpenSettings?: () => void;
}

export function PlatformDashboard({ platformLabel, tokenHint, platform, settings, snapshots, lookbackDays = 7, onOpenSettings }: PlatformDashboardProps) {
  const [tab, setTab] = useState<Tab>('overview');
  const [messages, setMessages] = useState<MessagingMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const loadMessages = useCallback(async () => {
    setLoadingMessages(true);
    try {
      const toDate = toLocalDateStr();
      const fromDate = toLocalDateStr(subDays(new Date(), lookbackDays - 1));
      const msgs = await getMessagesByDateRange(platform, fromDate, toDate);
      setMessages(msgs);
    } finally {
      setLoadingMessages(false);
    }
  }, [platform, lookbackDays]);

  useEffect(() => {
    if (tab === 'messages') loadMessages();
  }, [tab, loadMessages]);

  // Auto-refresh messages after each poll so new thread replies appear immediately
  useEffect(() => {
    return messagingMonitor.on((event, data) => {
      if (event === 'poll_complete' && tab === 'messages') {
        const d = data as { platform?: string };
        if (!d.platform || d.platform === platform) loadMessages();
      }
    });
  }, [tab, platform, loadMessages]);
  const latest = snapshots[0] ?? null;
  const prev = snapshots[1] ?? null;

  if (!settings?.enabled || !settings.accessToken) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-8">
        <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-4">
          <FontAwesomeIcon icon={faComment} className="h-7 w-7 text-indigo-400"/>
        </div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">{platformLabel} not connected</h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 max-w-sm mb-6">{tokenHint}</p>
        <button onClick={onOpenSettings} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors">
          <FontAwesomeIcon icon={faGear} className="h-3.5 w-3.5"/>Configure in Settings
        </button>
      </div>
    );
  }

  const TABS: {id:Tab;label:string;badge?:number}[] = [
    {id:'overview',label:'Overview'},
    {id:'channels',label:'Channels',badge:latest?.channelStats.length},
    {id:'members',label:'Members',badge:latest?.memberStats.length},
    {id:'anomalies',label:'Anomalies',badge:latest?.anomalies.filter(a=>a.severity!=='low').length||undefined},
    {id:'messages',label:'Messages'},
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex items-center gap-1 px-4 pt-3 pb-0 border-b border-gray-200 dark:border-slate-700/50 flex-shrink-0">
        {TABS.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)}
            className={cx('flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
              tab===t.id ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200')}>
            {t.label}
            {t.badge!=null && t.badge>0 && (
              <span className={cx('text-[10px] px-1.5 rounded-full font-semibold', t.id==='anomalies' ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400')}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* ── Overview ── */}
        {tab==='overview' && !latest && (
          <div className="text-center py-10">
            <div className="h-6 w-6 border-2 border-gray-200 dark:border-slate-600 border-t-indigo-500 rounded-full animate-spin mx-auto mb-3"/>
            <p className="text-sm text-gray-400 dark:text-slate-500">Waiting for first poll…</p>
            <p className="text-xs text-gray-300 dark:text-slate-600 mt-1">Last polled: {settings.lastPolledAt ? new Date(settings.lastPolledAt).toLocaleString('en-AU') : 'never'}</p>
          </div>
        )}

        {tab==='overview' && latest && <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Stat label="Messages today" value={latest.totalMessages.toLocaleString()} icon={<FontAwesomeIcon icon={faComment} className="h-4 w-4 text-indigo-500"/>} bg="bg-indigo-500/10"/>
            <Stat label="Reactions" value={(latest.totalReactions ?? 0).toLocaleString()} icon={<FontAwesomeIcon icon={faThumbsUp} className="h-4 w-4 text-violet-500"/>} bg="bg-violet-500/10"/>
            <Stat label="Active members" value={latest.activeMembers} icon={<FontAwesomeIcon icon={faUsers} className="h-4 w-4 text-emerald-500"/>} bg="bg-emerald-500/10"/>
            <Stat label="Active channels" value={latest.activeChannels} icon={<FontAwesomeIcon icon={faHashtag} className="h-4 w-4 text-sky-500"/>} bg="bg-sky-500/10"/>
            <Stat label="Anomalies" value={latest.anomalies.length} icon={<FontAwesomeIcon icon={faBell} className="h-4 w-4 text-amber-500"/>} bg="bg-amber-500/10"/>
          </div>

          {snapshots.length > 1 && (
            <Card title="7-day message volume">
              <div className="p-4 flex items-end gap-2 h-24">
                {[...snapshots].reverse().map(s => {
                  const m = Math.max(...snapshots.map(x=>x.totalMessages), 1);
                  return (
                    <div key={s.date} className="flex flex-col items-center gap-1 flex-1">
                      <div className="w-full rounded-t-sm bg-indigo-400 dark:bg-indigo-500 transition-all" style={{height:`${Math.max(4,(s.totalMessages/m)*64)}px`}}/>
                      <span className="text-[9px] text-gray-400 dark:text-slate-500 truncate w-full text-center">{s.date.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {latest.memberStats.length > 0 && (
            <Card title="Top contributors today">
              <div className="p-4 space-y-2.5">
                {[...latest.memberStats].sort((a,b)=>b.messageCount-a.messageCount).slice(0,5).map((m) => {
                  const hue = m.memberId.split('').reduce((a,c)=>a+c.charCodeAt(0),0)%360;
                  const maxV = Math.max(...latest.memberStats.map(x=>x.messageCount), 1);
                  const d = prev?.memberStats.find(x=>x.memberId===m.memberId);
                  const delta = d != null ? m.messageCount - d.messageCount : null;
                  return (
                    <div key={m.memberId} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0" style={{background:`hsl(${hue},55%,46%)`}}>
                        {m.memberName[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-medium text-gray-800 dark:text-slate-200 truncate">{m.memberName}</span>
                          <span className="text-xs text-gray-500 dark:text-slate-400 ml-2 flex-shrink-0">
                            {m.messageCount}
                            {delta != null && delta !== 0 && <span className={cx('ml-1 text-xs', delta>0?'text-emerald-500':'text-red-400')}>{delta>0?'+':''}{delta}</span>}
                          </span>
                        </div>
                        <div className="h-1 rounded-full bg-gray-100 dark:bg-slate-700 overflow-hidden">
                          <div className="h-full rounded-full bg-indigo-400 dark:bg-indigo-500 transition-all" style={{width:`${(m.messageCount/maxV)*100}%`}}/>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {latest.anomalies.filter(a=>a.severity!=='low').length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-widest mb-2">
                ⚠️ {latest.anomalies.filter(a=>a.severity!=='low').length} anomalies require attention
              </p>
              <div className="space-y-1">
                {latest.anomalies.filter(a=>a.severity!=='low').slice(0,3).map((a,i) => (
                  <p key={i} className="text-xs text-amber-700 dark:text-amber-300">{a.description}</p>
                ))}
              </div>
              <button onClick={()=>setTab('anomalies')} className="mt-2 text-xs text-amber-600 dark:text-amber-400 underline underline-offset-2">View all →</button>
            </div>
          )}
        </>}

        {tab==='channels' && <Card title="Channel Activity" sub="Today's message counts across all monitored channels"><ChannelTable rows={latest?.channelStats??[]} anomalies={latest?.anomalies??[]}/></Card>}
        {tab==='members' && <Card title="Member Activity" sub="Top 20 most active members today"><MemberTable rows={latest?.memberStats??[]} anomalies={latest?.anomalies??[]}/></Card>}
        {tab==='anomalies' && (
          <>
            <Card title="Anomalies" sub={`${latest?.anomalies.length ?? 0} detected · sorted by severity`}>
              <div className="p-4"><AnomalyList items={latest?.anomalies??[]}/></div>
            </Card>
            <DetectorReference />
          </>
        )}
        {tab==='messages' && (
          <Card title="Messages" sub={`Last ${lookbackDays} days · ${messages.length} messages`}>
            {loadingMessages
              ? <div className="flex items-center justify-center py-10"><div className="h-5 w-5 border-2 border-gray-200 dark:border-slate-600 border-t-indigo-500 rounded-full animate-spin"/></div>
              : <div className="p-3"><MessageFeed messages={messages} channelStats={latest?.channelStats}/></div>
            }
          </Card>
        )}
      </div>
    </div>
  );
}
